/**
 *------------------------------------------------------------------------
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2018, 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *------------------------------------------------------------------------
 */
import StringResources from 'nls/StringResources';
import { getGroupLabel, getFieldLabel } from './RelationshipUtil';

const LONGPRESSTIME = 500;
const DATASET_NODES = 'data.nodes';
const DATASET_LINK = 'data.link';
const HIGHLIGHTED = 'highlighted';
const SELECTED = 'selected';
const HAS_SELECTION = 'hasSelection';
const MAP_GROUPTYPES_TO_LOCALIZED_GROUPTYPES = {
	group: StringResources.get('group'),
	hierarchy: StringResources.get('hierarchy'),
	concept: StringResources.get('concept'),
	fact: StringResources.get('fact')
};

export default class RelationshipGraphEventHandler {

	constructor(props) {
		this.widget = props.widget;
		this.startingPointsVM = props.startingPointsVM;
		this._selectedDataPoints = [];
		this._lastHoverItem = null;
		this.nodeDataset = props.nodeDataset;
		if (this.nodeDataset && this.widget) {
			// the initial state of the relationship diagram should not have selected nodes
			this.nodeDataset.decorate(HAS_SELECTION, false);
			this.widget.render('client');
		}
		this.dataProvider = props.dataProvider;
		this._isLongPress = false;
	}

	_getClientX(event) {
		return (event.hasOwnProperty('clientX')) ? event.clientX : null;
	}

	_getClientY(event) {
		return (event.hasOwnProperty('clientY')) ? event.clientY : null;
	}

	_getEventTargets(event) {
		const visCoord = this._getClientX(event) && this._getClientY(event) ? this.widget.getVisCoordinate(this._getClientX(event), this._getClientY(event)) : null;
		const items = visCoord && this.widget.getItemsAtPoint(visCoord);
		return items && items.length ? items : null;
	}

	_processItems(items) {
		let dataPointItems = [];
		if (items.length === 1) {
			dataPointItems = this._processNode(items[0]);
		} else if (items.length === 3) {
			dataPointItems = this._processLink(items);
		}
		return dataPointItems && dataPointItems.length ? dataPointItems : null;
	}

	_processNode(item) {
		const dataPointItems = [];
		const values = this._itemRowToTargetValues(item.row);
		if (!values) {
			return null;
		}
		const isFieldNode = this._isFieldNode(values);
		dataPointItems.push({
			dataset: DATASET_NODES,
			index: item.index,
			source: item.source,
			values: values,
			itemId: this._getNodeId(values[0], isFieldNode),
			isFieldNode: isFieldNode
		});
		return dataPointItems;
	}

	/**
	* Check if the current node is a Field node
	**/
	_isFieldNode(values){
		return !(this.dataProvider.getRawNodeDataset().getOptions().showConcepts)
			|| values[2] && values[2][0] && values[2][0].u === 'field'
			|| values[0].length === 2;
	}

	_getNodeId(values, isFieldNode) {
		let result = values.length === 1 ? values[0].u : values[1].u;
		if(isFieldNode){
			result = this._trimFieldPrefix(result);
		}
		return result;
	}

	_processLink(items) {
		const dataPointItems = [];
		// VIPR returns 3 items for a link: 1st item is from node, 2nd is to node and 3rd item is the link.
		// The 3rd item contains all information that we need, so process only the 3rd item
		const values = this._itemRowToTargetValues(items[2].row);
		if (!values) {
			return null;
		}
		dataPointItems.push({
			dataset: DATASET_LINK,
			source: items[2].source,
			values: values[2],
			itemId: { fromNode_id: this._getNodeId(values[0]), toNode_id: this._getNodeId(values[1]) }
		});
		return dataPointItems;
	}

	_itemRowToTargetValues(row) {
		if (!row || !row.length) {
			return null;
		}
		return row.map(data => {
			if (data.infoType === 'value') {
				var values = [];
				var index = data.index || 0;
				values[index] = data.value;
				return values;
			}
			return data.items.map(item => {
				return {
					u: item.source.getUniqueName(),
					d: item.source.item.d,
					p: item.source.item.p
				};
			});
		});
	}

	_getEventItems(event) {
		const viprItems = this._getEventTargets(event);
		if (viprItems) {
			return this._processItems(viprItems);
		}
		return null;
	}

	_setContextMenu({ event, field }) {
		this.startingPointsVM.relationship.setContextMenu({ element: event.target, field });
	}

	_hideContextMenu() {
		this.startingPointsVM.relationship.setContextMenu({ element: null, field: null });
	}

	_enableSelection(dataPointItems) {
		dataPointItems.forEach(dataPoint => {
			dataPoint.source.decorate(HIGHLIGHTED);
			dataPoint.source.decorate(SELECTED, true);
			// add selected item in the list of selected items
			this._selectedDataPoints.push({
				itemId: dataPoint.itemId,
				source: dataPoint.source
			});
		});
		this.nodeDataset.decorate(HAS_SELECTION, true);
		this.widget.render('client');
		this.startingPointsVM.relationship.setSelectedFields(this._selectedDataPoints);
		const influencers = this._getDistinctInfluencers(this._selectedDataPoints).map(
			id => ({
				id,
				label: this._getFieldLabel(id)
			})
		);
		this.startingPointsVM.setSelectedInfluencers(influencers);
	}

	// We can have the same field in different groups, this is to avoid sending Smarts many nodes with same Id.
	// Otherwise Smarts will return visualization "relationship between Quantity and Quantity"
	_getDistinctInfluencers(selectedItems) {
		const influencers = {};
		selectedItems.forEach(selected => {
			if (!influencers[selected.itemId]) {
				influencers[selected.itemId] = selected.itemId;
			}
		});
		return Object.keys(influencers);
	}

	_disableSelection(dataPointItems) {
		dataPointItems.forEach(dataPoint => {
			dataPoint.source.clearDecorations();
			this._selectedDataPoints = this._selectedDataPoints.filter(selected => {
				return selected.source !== dataPoint.source;
			});
		});
		if (!this._selectedDataPoints.length) {
			this.nodeDataset.decorate(HAS_SELECTION, false);
		}
		this.widget.render('client');
		this.startingPointsVM.relationship.setSelectedFields(this._selectedDataPoints);
		const influencers = this._getDistinctInfluencers(this._selectedDataPoints).map(
			id => ({
				id,
				label: this._getFieldLabel(id)
			})
		);
		this.startingPointsVM.setSelectedInfluencers(influencers);
	}

	_isSelected(dataPointItem) {
		return dataPointItem.source.getDecoration(SELECTED);
	}

	_disableHighlight(item) {
		item.source.decorate(HIGHLIGHTED, false);
		this._lastHoverItem = null;
		this.widget.render('client');

	}

	_enableHighlight(item) {
		if (this._lastHoverItem && !this._isSelected(this._lastHoverItem)) {
			this._lastHoverItem.source.decorate(HIGHLIGHTED, false);
		}
		item.source.decorate(HIGHLIGHTED);
		this._lastHoverItem = item;
		this.widget.render('client');
	}

	// We want to display tooltip on top and center of the node
	_getAdjustedCoordinatesForNodes(dataPointItem, event) {
		const coord = {};
		if (dataPointItem.dataset === DATASET_NODES) {
			const rec = event.target.getBoundingClientRect();
			if (rec) {
				coord.y = rec.top;
				coord.x = (rec.left + rec.right) / 2;
			}
		}
		return coord;
	}

	removeTooltip() {
		if (this._lastHoverItem && !this._isSelected(this._lastHoverItem)) {
			this._disableHighlight(this._lastHoverItem);
		}
		this.startingPointsVM.relationship.tooltip.showTooltip(null);
	}

	// Event handler
	onClick(event) {
		const dataPointItems = this._getEventItems(event);
		if (!dataPointItems || !this._acceptClick(dataPointItems)) {
			return;
		}
		// multi-selection mode flag
		var bCtrlClick = event.ctrlKey || event.metaKey;
		const isSelected = this._isSelected(dataPointItems[0]);
		if (isSelected) {
			// select a selected node triggers de-select this node

			this._disableSelection(dataPointItems);
		} else {
			if (bCtrlClick) {
				// multi-selection mode: enable newly selected
				this._enableSelection(dataPointItems);
			} else {
				// single-selection mode: disable previous selected and enable newly selected
				if (this._selectedDataPoints.length > 0) {
					this._disableSelection(this._selectedDataPoints);
				}
				this._enableSelection(dataPointItems);
			}
		}
	}

	/**
	 * Handle context menu event
	 * @param {object} event
	 */
	onRightClick(event) {
		const dataPointItems = this._getEventItems(event);
		if (!dataPointItems || !this._acceptClick(dataPointItems)) {
			return;
		}
		this._setContextMenu({ event, field: dataPointItems[0] });
	}

	// Accept only node element in the graph which is a field node (not a group/concept/hierarchy node)
	_acceptClick(dataPointItems) {
		// user can only click 1 element at a time.
		const item = dataPointItems[0];
		return item.dataset === DATASET_NODES && item.isFieldNode;
	}

	onMouseOver(event) {
		const dataPointItems = this._getEventItems(event);
		if (!dataPointItems) {
			return null;
		}
		this._displayTooltip(event, dataPointItems[0]);

	}

	onMouseOut() {
		this.removeTooltip();
	}

	onTouchStart = (event) => {
		if (!event.touches || event.touches.length !== 1) {
			return;
		}
		this.removeTooltip();

		// Convert touch event coordinates to be event.clientX and event.clientY which is used by VIDA
		event = this._normalizeTouchEvents(event);

		// Persist the event target for accessing the event properties in an asynchronous way
		const target = event.target;

		const dataPointItems = this._getEventItems(event);
		if (!dataPointItems) {
			return;
		}
		const dataPointItem = dataPointItems[0];
		if (dataPointItem.dataset === DATASET_NODES && dataPointItem.isFieldNode) {
			const isSelected = this._isSelected(dataPointItem);
			this.pressTimer = setTimeout(() => {
				this._isLongPress = true;
				if (this._touchSameNode(dataPointItem)) {
					if (!isSelected) {
						if(this._selectedDataPoints && this._selectedDataPoints.length === 0) {
							// User long presses an unselected node when no nodes were selected: node is selected and context menu shows
							this._setContextMenu({ event: { target }, field: dataPointItem });
							this.removeTooltip();
						} else {
							// User long presses an unselected node when at least one node was already selected: existing selection is preserved, node is now also selected, context menu is not shown
							this._hideContextMenu();
						}
						this._enableSelection([dataPointItem]);
					} else {
						this._disableSelection([dataPointItem]);
						this.removeTooltip();
						this._hideContextMenu();
					}
				}
			}, LONGPRESSTIME);
		}

		this._displayTooltip(event, dataPointItems[0]);
		event.preventDefault();
	}

	onTouchEnd = (event) => {
		clearTimeout(this.pressTimer);

		event = this._normalizeTouchEvents(event);

		if (this._isLongPress) {
			this._isLongPress = false;
			return;
		}

		const dataPointItems = this._getEventItems(event);
		if (!dataPointItems) {
			// NOTE: Do not remove the node selection here!
			// Removing the node selection will make it impossible to interact with the recommended visualization menu on iPad.
			// To remove a selected node on iPad, you simply have to press and hold the node and it will be deselected.
			this._hideContextMenu();
			this.removeTooltip();
			return;
		}

		const dataPointItem = dataPointItems[0];
		if (dataPointItem.dataset === DATASET_NODES && dataPointItem.isFieldNode) {
			if (this._selectedDataPoints.length > 0) {
				this._disableSelection(this._selectedDataPoints);
			}

			// Any touch on any node closes context menu
			this._hideContextMenu();
			this._enableSelection(dataPointItems);
		}
	}

	onKeyDown(event) {
		if(event.key === 'Delete' || event.key === 'Backspace') {
			const relationshipVM = this.startingPointsVM.relationship;
			const selectedFields = relationshipVM.selectedFields;
			if(selectedFields && selectedFields.length === 1) {
				relationshipVM.canDeleteFields(selectedFields) && relationshipVM.deleteFields(selectedFields);
			}
			event.stopPropagation();
		} else if (event.key === 'Enter') {
			const { left, top, width, height } = event.target.getBoundingClientRect();
			event.clientX = left + width / 2;
			event.clientY = top + height / 2;
			const dataPointItems = this._getEventItems(event);
			if (dataPointItems && dataPointItems.length) {
				const dataPointItem = dataPointItems[0];
				if (dataPointItem.dataset === DATASET_NODES && dataPointItem.isFieldNode) {
					this._setContextMenu({ event, field: dataPointItem });
					event.stopPropagation();
				}
			}
		}
	}

	_normalizeTouchEvents(e) {
		const event = e;
		event.isTouch = true;
		const coords = e.touches[0];

		if (coords && coords.clientX !== undefined) {
			event.clientX = coords.clientX;
			event.clientY = coords.clientY;
			event.pageX = coords.pageX;
			event.pageY = coords.pageY;
		}
		return event;
	}

	_touchSameNode(dataPointItem) {
		return this._lastHoverItem && this._lastHoverItem.source === dataPointItem.source;
	}

	_displayTooltip(event, item) {
		const tooltipInfo = this._buildTooltipInfo(event, item);
		if (tooltipInfo) {
			this._enableHighlight(item);
			this.startingPointsVM.relationship.tooltip.showTooltip(tooltipInfo);
		}
	}

	_buildTooltipInfo(event, item) {
		const tooltipTitle = this._buildTooltipTitle(item);
		event.persist(); // keep target for tooltip's flyout node
		if (tooltipTitle) {
			const adjustedCoord = this._getAdjustedCoordinatesForNodes(item, event);
			return {
				title:  tooltipTitle,
				coordinates: {
					top: adjustedCoord.y || event.clientY,
					left: adjustedCoord.x || event.clientX,
					width: 0
				},
				eventTarget: event.target,
				itemId: item.itemId
			};
		} else {
			return null;
		}
	}

	_buildTooltipTitle(dataPointItem) {
		if (dataPointItem.dataset === DATASET_NODES) {
			if (dataPointItem.isFieldNode) {
				return this._buildTooltipTitleForFieldNode(dataPointItem);
			} else {
				return this._buildTooltipTitleForGroupNode(dataPointItem);
			}
		}
		return this._buildTooltipTitleForLink(dataPointItem);
	}

	// For Field node, tooltip should look like:
	// Parent: parentId (ex: Table1)
	// Field label
	// Relevance: confidence value, in format nn% (ex: 85%)
	_buildTooltipTitleForFieldNode(dataPointItem) {
		const itemId = this._trimFieldPrefix(dataPointItem.itemId);
		return [
			{
				label: `${StringResources.get('parent')}: `,
				value: `${this._getParentId(itemId)}`
			},
			{
				label: '',
				value: `${this._getFieldLabel(itemId)}`
			}
		];
	}

	/*
	 * Before relationship API have an ideal way to indicate a column is a field.
	 * we will need to do this trimming since a field column is always fixed width
	 * none_ prefix
	 */
	_trimFieldPrefix(itemId){
		if(itemId && itemId.indexOf('none_') === 0){
			//TODO: By the new design, we always prefix none_ for field ID,
			//The workaround should be removed after server end
			//introduces a new indicator for field. The background
			//of this is that 'predict' is currently still analyzing group nodes
			//while those nodes do not exist any more from node datasets.
			itemId = itemId.substr(5, itemId.length);
		}
		return itemId;
	}

	/**
	 * For group node, tooltip should look like:
	 * {groupType}: {groupId}
	 * Ex: "Concept: Geography"
	 * For hierarchy, the tooltip should look like:
	 * Hierarchy: <highest level label> - <lowest level label>
	 * Ex: "Hierarchy: Product Line - Product"
	 */
	_buildTooltipTitleForGroupNode(dataPointItem) {
		const groupType = this._getGroupType(dataPointItem);
		const localizedGroupType = MAP_GROUPTYPES_TO_LOCALIZED_GROUPTYPES[groupType];
		const groupLabel = this._getGroupLabel(dataPointItem);
		if (groupType === 'hierarchy') {
			const levelString = this.dataProvider.rawNodeDataset.getHierarchyLevels(dataPointItem.itemId);
			return [
				{
					label: `${localizedGroupType}: `,
					value: `${levelString || groupLabel}`
				}
			];
		}
		return [
			{
				label: `${localizedGroupType}: `,
				value: `${groupLabel}`
			}
		];
	}

	// For link, tooltip should look like:
	// fromGroup_name - toGroup_name: name of 2 groups at 2 ends of the link
	// Strength: 57% (strongest relationship between 2 groups calculated by Predicts)
	_buildTooltipTitleForLink(dataPointItem) {
		return [
			{
				label: '',
				value: `${this._getGroupLabelFromGroupId(dataPointItem.itemId.fromNode_id)} - ${this._getGroupLabelFromGroupId(dataPointItem.itemId.toNode_id)}`
			},
			{
				label: `${StringResources.get('strength')}: `,
				value: `${this._format(dataPointItem.values[0])}`
			}
		];
	}

	_getGroupLabelFromGroupId(groupUid) {
		const fields = this.startingPointsVM.fields.getFields().getAll();
		return getGroupLabel(groupUid, fields);
	}

	_getFieldLabel(fieldId) {
		const fields = this.startingPointsVM.fields.getFields().getAll();
		return getFieldLabel(fieldId, fields);
	}

	_getParentId(fieldId) {
		return fieldId.substr(0, fieldId.lastIndexOf('.'));
	}

	// Format the confidence and strength value
	// Ex: value = 0.6677797 => return 67%
	_format(value) {
		if (!this.startingPointsVM.relationship.getIsFilterStrengthFeatureEnabled()) {
			if (!isNaN(value)) {
				return `${Math.round(value * 100)}%` ;
			}
		}
		return `${value}%`;
	}

	_getGroupType(dataPointItem) {
		return dataPointItem.values[2][0].u;
	}

	_getGroupLabel(dataPointItem) {
		return dataPointItem.values[0][0].d;
	}

	onDestroy() {
		this.startingPointsVM.setSelectedInfluencers();
	}

}



// WEBPACK FOOTER //
// ./src/js/components/startingPoints/relationshipGraph/RelationshipGraphEventHandler.js